import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Mail, Reply, Upload, UserPlus, Workflow } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { STAGE_LABEL, parseCsv, roleLabel, useAdminConfig, usePeople, usePerson, type ContactStage, type Person } from "../../lib/admin";
import { fmtNumber } from "../../lib/format";
import { CHANNEL_LABEL } from "../../lib/bookings";
import { BookSomeoneIn, PersonBookings } from "./book-components";
import {
  Ago,
  BackLink,
  Badge,
  Button,
  Card,
  CardHeader,
  CardList,
  CardRow,
  CardSection,
  Checkbox,
  Drawing,
  Empty,
  ErrorState,
  Fact,
  FactList,
  Field,
  Input,
  Modal,
  PageHeader,
  SaveStatus,
  SearchInput,
  Segmented,
  SelectField,
  Skeleton,
  SortHeader,
  Table,
  TableScroll,
  TableSkeletonRows,
  Textarea,
  cx,
  notifyError,
  toaster,
  useConfirm,
  useSaveStatus,
  useTableSort,
} from "../../components/ui";
import {
  ComposeModal,
  EnrollmentStatusBadge,
  usePageTitle,
  EnrollModal,
  FollowUpInput,
  Health,
  LifecycleBadge,
  LogActivity,
  PlayChips,
  SignalChips,
  StageBadge,
  TaskList,
  Timeline,
  invalidateAdmin,
} from "./components";

type SortKey = "name" | "stage" | "health" | "signedUpAt" | "lastSeenAt" | "lastContactedAt";
const STAGE_ORDER = Object.keys(STAGE_LABEL);
const SORTERS: Record<SortKey, (p: Person) => number | string> = {
  name: (p) => (p.name ?? p.email).toLowerCase(),
  stage: (p) => STAGE_ORDER.indexOf(p.stage),
  health: (p) => p.account?.health ?? -1,
  signedUpAt: (p) => p.signedUpAt ?? p.createdAt,
  lastSeenAt: (p) => p.lastSeenAt ?? 0,
  lastContactedAt: (p) => p.lastContactedAt ?? 0,
};

export function AdminPeople() {
  const q = usePeople();
  usePageTitle("People");
  const config = useAdminConfig();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [stage, setStage] = useState("all");
  const [play, setPlay] = useState("all");
  // A new column starts A–Z for name and stage, newest/highest first otherwise.
  const { sort, sortBy, sorted } = useTableSort<SortKey>({ key: "signedUpAt", dir: -1 }, (k) => (k === "name" || k === "stage" ? 1 : -1));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<"add" | "import" | "enroll" | null>(null);

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    const filtered = (q.data ?? [])
      .filter((p) => source === "all" || (source === "signup" ? p.source === "signup" : p.source !== "signup"))
      .filter((p) => stage === "all" || p.stage === stage)
      .filter((p) => play === "all" || p.plays.includes(play as Person["plays"][number]))
      .filter((p) => !s || [p.name, p.email, p.company, p.title, ...p.tags].some((v) => v?.toLowerCase().includes(s)));
    return sorted(filtered, (r, k) => SORTERS[k](r));
  }, [q.data, search, source, stage, play, sort]); // `sorted` reads `sort`

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const counts = useMemo(() => {
    const d = q.data ?? [];
    return { all: d.length, signup: d.filter((p) => p.source === "signup").length, prospect: d.filter((p) => p.source !== "signup").length };
  }, [q.data]);

  return (
    <>
      <PageHeader
        title="People"
        subtitle="Every sign-up plus the prospects you add. One timeline per person."
        actions={
          <>
            <Button onClick={() => setModal("import")}>
              <Upload size={14} /> Import CSV
            </Button>
            <Button variant="primary" onClick={() => setModal("add")}>
              <UserPlus size={14} /> Add prospect
            </Button>
          </>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchInput label="Search people" wrapperClassName="w-full @xl:w-72" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, company, tag" />
        <Segmented
          aria-label="Source filter"
          items={[
            { value: "all", label: `All ${counts.all}` },
            { value: "signup", label: `Sign-ups ${counts.signup}` },
            { value: "prospect", label: `Prospects ${counts.prospect}` },
          ]}
          value={source}
          onChange={setSource}
        />
        <SelectField
          aria-label="Stage"
          className="w-36"
          items={[{ value: "all", label: "Any stage" }, ...Object.entries(STAGE_LABEL).map(([value, label]) => ({ value, label }))]}
          value={stage}
          onChange={setStage}
        />
        <SelectField
          aria-label="Play"
          className="w-48"
          items={[{ value: "all", label: "Any play" }, ...(config.data?.playbooks ?? []).map((p) => ({ value: p.id, label: p.name }))]}
          value={play}
          onChange={setPlay}
        />
      </div>

      {selected.size > 0 && (
        <div className="sticky top-12 z-20 mb-3 flex items-center justify-between gap-3 rounded-lg border border-line-strong bg-surface px-3 py-2 shadow-sm lg:top-2">
          <span className="text-sm text-ink" role="status" aria-live="polite">
            {selected.size} selected
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button size="sm" variant="primary" onClick={() => setModal("enroll")}>
              <Workflow size={13} /> Add to sequence
            </Button>
          </div>
        </div>
      )}

      <Card>
        {q.isError ? (
          <ErrorState error={q.error} />
        ) : (
          <TableScroll label="People">
            <Table caption="People, sortable by column. Select a name to open their timeline." busy={q.isPending} className="min-w-[1000px]">
              <thead>
                <tr>
                  <th scope="col" className="w-10 py-1 pl-3">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} hideLabel>
                      Select all shown
                    </Checkbox>
                  </th>
                  <SortHeader {...sortBy("name")}>Person</SortHeader>
                  <SortHeader {...sortBy("stage")}>Stage</SortHeader>
                  <SortHeader {...sortBy("health")}>Account</SortHeader>
                  <SortHeader {...sortBy("signedUpAt")}>Added</SortHeader>
                  <SortHeader {...sortBy("lastSeenAt")}>Last login</SortHeader>
                  <SortHeader {...sortBy("lastContactedAt")}>Last contacted</SortHeader>
                  <th scope="col">Plays</th>
                </tr>
              </thead>
              <tbody>
                {q.isPending && <TableSkeletonRows rows={6} columns={8} />}
                {rows.map((p) => (
                  <tr key={p.id} className={cx("cursor-pointer hover:bg-surface-2", selected.has(p.id) && "bg-accent-wash")} onClick={() => navigate(`/admin/people/${p.id}`)}>
                    <td className="py-1 pl-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} hideLabel>
                        Select {p.name ?? p.email}
                      </Checkbox>
                    </td>
                    <td>
                      <Link to={`/admin/people/${p.id}`} className="font-medium text-ink hover:underline" onClick={(e) => e.stopPropagation()}>
                        {p.name ?? p.email}
                      </Link>
                      {p.unsubscribed && (
                        <>
                          {" "}
                          <Badge tone="bad">Unsubscribed</Badge>
                        </>
                      )}
                      <div className="truncate text-xs text-ink-3">
                        {p.email}
                        {p.title && ` · ${p.title}`}
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col items-start gap-1">
                        <StageBadge value={p.stage} />
                        <span className="text-[11px] text-ink-3">{p.source === "signup" ? "Sign-up" : p.source === "import" ? "Imported" : p.channel ? CHANNEL_LABEL[p.channel] : "Added"}</span>
                      </div>
                    </td>
                    <td>
                      {p.account ? (
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2 text-[13px] text-ink">
                            <span className="max-w-40 truncate">{p.account.name}</span>
                            <LifecycleBadge value={p.account.lifecycle} />
                          </div>
                          <Health score={p.account.health} compact />
                        </div>
                      ) : (
                        <span className="text-xs text-ink-3">{p.company ?? "No workspace"}</span>
                      )}
                    </td>
                    <td className="text-xs text-ink-2">
                      <Ago t={p.signedUpAt ?? p.createdAt} />
                    </td>
                    <td className="text-xs text-ink-2">
                      <Ago t={p.lastSeenAt} empty={p.userId ? "never" : "–"} />
                    </td>
                    <td className="text-xs text-ink-2">
                      <Ago t={p.lastContactedAt} />
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <PlayChips plays={p.plays} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {q.data && !rows.length && <Empty title="No one matches">Try a different filter, or add a prospect.</Empty>}
          </TableScroll>
        )}
      </Card>

      <AddPersonModal open={modal === "add"} onOpenChange={(o) => setModal(o ? "add" : null)} />
      <ImportModal open={modal === "import"} onOpenChange={(o) => setModal(o ? "import" : null)} />
      <EnrollModal open={modal === "enroll"} onOpenChange={(o) => setModal(o ? "enroll" : null)} contactIds={[...selected]} onDone={() => setSelected(new Set())} />
    </>
  );
}

function AddPersonModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", name: "", company: "", title: "", linkedin: "", notes: "" });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [saving, setSaving] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const { id } = await api<{ id: string }>("/admin/people", { method: "POST", json: form });
      await invalidateAdmin(qc);
      onOpenChange(false);
      setForm({ email: "", name: "", company: "", title: "", linkedin: "", notes: "" });
      navigate(`/admin/people/${id}`);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Add a prospect" description="Someone you want to reach who hasn't signed up. They link to their account automatically if they do.">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Email">
          <Input type="email" required value={form.email} onChange={set("email")} placeholder="founder@company.com" />
        </Field>
        <div className="grid gap-3 @xl:grid-cols-2">
          <Field label="Name">
            <Input value={form.name} onChange={set("name")} />
          </Field>
          <Field label="Company">
            <Input value={form.company} onChange={set("company")} />
          </Field>
          <Field label="Title">
            <Input value={form.title} onChange={set("title")} />
          </Field>
          <Field label="LinkedIn URL">
            <Input value={form.linkedin} onChange={set("linkedin")} />
          </Field>
        </div>
        <Field label="Notes">
          <Textarea value={form.notes} onChange={set("notes")} rows={3} />
        </Field>
        <div className="flex justify-end">
          <Button variant="primary" disabled={saving}>{saving ? "Adding…" : "Add prospect"}</Button>
        </div>
      </form>
    </Modal>
  );
}

const CSV_COLUMNS: Record<string, string[]> = {
  email: ["email", "email_address", "e_mail"],
  name: ["name", "full_name", "contact"],
  company: ["company", "company_name", "organization", "organisation"],
  title: ["title", "job_title", "role", "position"],
  domain: ["domain", "website", "company_domain"],
  linkedin: ["linkedin", "linkedin_url", "linkedin_profile"],
};

function ImportModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => {
    return parseCsv(text)
      .map((r) => {
        const get = (k: string) => CSV_COLUMNS[k]!.map((c) => r[c]).find(Boolean) ?? "";
        const first = r.first_name ?? "";
        const last = r.last_name ?? "";
        return {
          email: get("email").toLowerCase(),
          name: get("name") || `${first} ${last}`.trim(),
          company: get("company"),
          title: get("title"),
          domain: get("domain"),
          linkedin: get("linkedin"),
        };
      })
      .filter((r) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.email));
  }, [text]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api<{ created: number; updated: number }>("/admin/people/import", {
        method: "POST",
        json: { rows, tags: tag.trim() ? [tag.trim()] : [] },
      });
      toaster.success({ title: `Imported ${res.created} new ${res.created === 1 ? "person" : "people"}`, description: res.updated ? `${res.updated} already existed and were enriched.` : undefined });
      await invalidateAdmin(qc);
      onOpenChange(false);
      setText("");
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Import prospects" description="Paste a CSV or choose a file. Needs an email column; name, company, title, domain and LinkedIn are picked up if present." wide>
      <div className="space-y-3">
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="CSV file"
          className="block text-sm text-ink-2 file:mr-3 file:rounded-lg file:border file:border-control file:bg-surface file:px-3 file:py-1.5 file:text-sm file:text-ink"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (f) setText(await f.text());
          }}
        />
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={7}
          aria-label="CSV text"
          placeholder={"email,name,company,title\nada@example.com,Ada Lovelace,Analytical Engines,Founder"}
          className="font-mono"
        />
        <Field label="Tag everyone in this import (optional)" hint="Handy for tracking a list's source, like a conference or a directory.">
          <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. saastr-2026" />
        </Field>
        <div className="flex items-center justify-between">
          <span className="text-xs text-ink-3" role="status" aria-live="polite">
            {text ? `${fmtNumber(rows.length)} valid row${rows.length === 1 ? "" : "s"}${rows.length > 2000 ? ", over the 2,000 limit" : ""}` : ""}
          </span>
          <Button variant="primary" onClick={submit} disabled={!rows.length || rows.length > 2000 || busy}>
            {busy ? "Importing…" : <>Import {rows.length ? fmtNumber(rows.length) : ""}</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// --- Person --------------------------------------------------------------------------------

export function AdminPerson() {
  const { personId } = useParams();
  const q = usePerson(personId!);
  const config = useAdminConfig();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const p = q.data;
  const [modal, setModal] = useState<"email" | "enroll" | "replied" | "book" | null>(null);
  const [profile, setProfile] = useState({ name: "", company: "", title: "", linkedin: "", notes: "", tags: "" });
  useEffect(() => {
    if (!p) return;
    setProfile({
      name: p.contact.name ?? "",
      company: p.contact.company ?? "",
      title: p.contact.title ?? "",
      linkedin: p.contact.linkedin ?? "",
      notes: p.contact.notes ?? "",
      tags: p.tags.join(", "),
    });
  }, [p]);

  const profileSave = useSaveStatus();
  const statusSave = useSaveStatus();
  usePageTitle(p ? (p.name ?? p.email) : null);

  /** Resolves false (after a toast) when the save fails. */
  const patch = async (json: Record<string, unknown>) => {
    try {
      await api(`/admin/people/${personId}`, { method: "PATCH", json });
      await invalidateAdmin(qc);
      return true;
    } catch (err) {
      notifyError(err);
      return false;
    }
  };
  const saveField = (k: keyof typeof profile) => () => {
    if (!p) return;
    const current = k === "tags" ? p.tags.join(", ") : ((p.contact as Record<string, unknown>)[k] ?? "");
    if (profile[k] === current) return;
    void profileSave.track(async () => {
      const ok = await patch(k === "tags" ? { tags: profile.tags.split(",").map((t) => t.trim()).filter(Boolean) } : { [k]: profile[k] });
      if (!ok) throw new Error("save failed");
    });
  };

  /** Stage and follow-up apply instantly; SaveStatus beside them announces it. */
  const patchStatus = (json: Record<string, unknown>) =>
    void statusSave.track(async () => {
      if (!(await patch(json))) throw new Error("save failed");
    });

  // The play that best fits them, to preselect a template.
  const template = config.data?.playbooks.find((pb) => p?.plays.includes(pb.id))?.template;

  if (q.isError)
    return q.error instanceof ApiError && q.error.status === 404 ? (
      <Empty title="Person not found" illustration={<Drawing name="notFound" />} />
    ) : (
      <Card>
        <ErrorState title="Couldn't load this person" error={q.error} />
      </Card>
    );

  return (
    <>
      <BackLink to="/admin/people" className="mb-3">
        All people
      </BackLink>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {p ? (p.name ?? p.email) : "…"} {p && <StageBadge value={p.stage} />}
            {p?.unsubscribed && <Badge tone="bad">Unsubscribed</Badge>}
          </span>
        }
        subtitle={
          p && (
            <>
              <a href={`mailto:${p.email}`} className="hover:underline">
                {p.email}
              </a>
              {p.title && ` · ${p.title}`}
              {p.company && ` · ${p.company}`}
              {p.contact.linkedin && (
                <a href={p.contact.linkedin} target="_blank" rel="noreferrer" className="ml-2 inline-flex min-h-6 min-w-6 items-center justify-center text-ink-3 hover:text-ink">
                  <ExternalLink size={13} aria-hidden />
                  <span className="sr-only">LinkedIn profile (opens in a new tab)</span>
                </a>
              )}
            </>
          )
        }
      />
      {p && (
        <div className="-mt-2 mb-5 flex flex-wrap items-center gap-2">
          <SelectField
            aria-label="Stage"
            className="w-36"
            items={(config.data?.contactStages ?? []).map((s) => ({ value: s, label: STAGE_LABEL[s] }))}
            value={p.stage}
            onChange={(v) => patchStatus({ stage: v as ContactStage })}
          />
          <FollowUpInput value={p.nextFollowUpAt} onChange={(t) => patchStatus({ nextFollowUpAt: t })} />
          <SaveStatus status={statusSave.status} />
          <Button onClick={() => setModal("replied")}>
            <Reply size={14} /> They replied
          </Button>
          <Button onClick={() => setModal("enroll")} disabled={p.unsubscribed}>
            <Workflow size={14} /> Sequence
          </Button>
          <Button variant="primary" onClick={() => setModal("email")} disabled={p.unsubscribed}>
            <Mail size={14} /> Email
          </Button>
        </div>
      )}
      {!p ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid gap-4 @4xl:grid-cols-[1fr_380px]">
          <div className="min-w-0 space-y-4">
            <Card>
              <FactList cols={4} className="p-4">
                <Fact term={p.userId ? "Signed up" : "Added"}>
                  <Ago t={p.signedUpAt ?? p.createdAt} />
                </Fact>
                <Fact term="Last login">{p.userId ? <Ago t={p.lastSeenAt} /> : <span className="text-ink-3">Not a user yet</span>}</Fact>
                <Fact term="Last contacted">
                  <Ago t={p.lastContactedAt} />
                </Fact>
                <Fact term="Workspaces">{p.accountCount}</Fact>
                {p.contact.channel && (
                  <Fact term="First met">
                    {CHANNEL_LABEL[p.contact.channel]}
                    {p.contact.channelNote && <span className="block text-xs text-ink-3">{p.contact.channelNote}</span>}
                  </Fact>
                )}
              </FactList>
              {!!p.plays.length && (
                <CardSection className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
                  Matches plays <PlayChips plays={p.plays} />
                </CardSection>
              )}
            </Card>

            {p.accounts.map((a) => (
              <Card key={a.id}>
                <CardHeader
                  title={
                    <Link to={`/admin/accounts/${a.id}`} className="hover:underline">
                      {a.name}
                    </Link>
                  }
                  subtitle={`${roleLabel(a.role)} · ${a.usage.apps} app${a.usage.apps === 1 ? "" : "s"} · ${a.usage.members} teammate${a.usage.members === 1 ? "" : "s"}`}
                  actions={
                    <>
                      <LifecycleBadge value={a.lifecycle} />
                      <Health score={a.health} compact />
                    </>
                  }
                />
                <CardSection className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-ink-2">
                  <span>
                    <span className="font-semibold text-ink tabular">{fmtNumber(a.usage.events7d)}</span> events this week
                  </span>
                  <span>
                    <span className="font-semibold text-ink tabular">{fmtNumber(a.usage.visitors30d)}</span> visitors 30d
                  </span>
                  <span>
                    last data <Ago t={a.usage.lastEventAt} />
                  </span>
                  <SignalChips signals={a.signals} max={4} />
                </CardSection>
                {!!a.apps.length && <CardSection flush className="px-4 py-2 text-xs text-ink-3">Apps: {a.apps.map((x) => x.name).join(", ")}</CardSection>}
              </Card>
            ))}

            <Card>
              <CardHeader title="Profile" subtitle="Each field saves when you leave it" actions={<SaveStatus status={profileSave.status} />} />
              <div className="grid gap-3 px-4 pb-4 @xl:grid-cols-2">
                {(["name", "company", "title", "linkedin"] as const).map((k) => (
                  <Field key={k} label={k === "linkedin" ? "LinkedIn URL" : k[0]!.toUpperCase() + k.slice(1)}>
                    <Input value={profile[k]} onChange={(e) => setProfile((f) => ({ ...f, [k]: e.target.value }))} onBlur={saveField(k)} />
                  </Field>
                ))}
                <div className="@xl:col-span-2">
                  <Field label="Tags" hint="Comma-separated">
                    <Input value={profile.tags} onChange={(e) => setProfile((f) => ({ ...f, tags: e.target.value }))} onBlur={saveField("tags")} />
                  </Field>
                </div>
                <div className="@xl:col-span-2">
                  <Field label="Notes">
                    <Textarea value={profile.notes} onChange={(e) => setProfile((f) => ({ ...f, notes: e.target.value }))} onBlur={saveField("notes")} rows={3} />
                  </Field>
                </div>
              </div>
            </Card>

            {!!p.enrollments.length && (
              <Card>
                <CardHeader title="Sequences" />
                <CardList>
                  {p.enrollments.map((e) => (
                    <CardRow key={e.id} className="text-sm">
                      <span className="text-ink">
                        {e.sequence_name}{" "}
                        <span className="text-xs text-ink-3">
                          · step {Math.min(e.step_index + (e.status === "active" ? 1 : 0), e.step_count ?? 0)} of {e.step_count}
                          {e.status === "active" && e.next_run_at && (
                            <>
                              {" "}
                              · next <Ago t={e.next_run_at} />
                            </>
                          )}
                        </span>
                      </span>
                      <span className="flex items-center gap-2">
                        <EnrollmentStatusBadge status={e.status} />
                        {e.status === "active" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Stop ${e.sequence_name}`}
                            onClick={async () => {
                              const ok = await confirm({
                                title: `Stop ${e.sequence_name} for ${p.name ?? p.email}?`,
                                description: "They get no more emails from it, and it can't be restarted.",
                                confirmLabel: "Stop",
                              });
                              if (!ok) return;
                              await api(`/admin/enrollments/${e.id}/stop`, { method: "POST" }).catch(notifyError);
                              await invalidateAdmin(qc);
                            }}
                          >
                            Stop
                          </Button>
                        )}
                      </span>
                    </CardRow>
                  ))}
                </CardList>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <PersonBookings contactId={p.id} onBook={() => setModal("book")} />
            <Card>
              <CardHeader title="Tasks" />
              <CardSection flush>
                <TaskList tasks={p.tasks} contactId={p.id} />
              </CardSection>
            </Card>
            <Card>
              <CardHeader title="Timeline" />
              <LogActivity path={`/admin/people/${p.id}/activities`} />
              <CardSection flush>
                <Timeline items={p.activity} />
              </CardSection>
            </Card>
          </div>
        </div>
      )}
      {p && (
        <>
          <ComposeModal open={modal === "email"} onOpenChange={(o) => setModal(o ? "email" : null)} contact={p} defaultTemplate={template} />
          <EnrollModal open={modal === "enroll"} onOpenChange={(o) => setModal(o ? "enroll" : null)} contactIds={[p.id]} />
          <RepliedModal open={modal === "replied"} onOpenChange={(o) => setModal(o ? "replied" : null)} contactId={p.id} />
          <BookSomeoneIn open={modal === "book"} onOpenChange={(o) => setModal(o ? "book" : null)} prefill={{ name: p.name, email: p.email }} />
        </>
      )}
      {dialog}
    </>
  );
}

function RepliedModal({ open, onOpenChange, contactId }: { open: boolean; onOpenChange: (o: boolean) => void; contactId: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api(`/admin/people/${contactId}/replied`, { method: "POST", json: { body: text || null } });
      toaster.success({ title: "Marked as replied", description: "Their sequences stopped and they moved to Engaged." });
      setText("");
      onOpenChange(false);
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="They replied" description="Logs the reply, stops any sequences, and moves them to Engaged if they were New or Contacted.">
      <div className="space-y-3">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} aria-label="Reply text" placeholder="Paste their reply or a summary (optional)" />
        <div className="flex justify-end">
          <Button variant="primary" onClick={submit} disabled={saving}>
            {saving ? "Logging…" : "Log reply"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
